import React, { useEffect, useRef } from 'react'
import { Box, Flex, Text, Button } from '../../vanilla'
import { observer } from 'mobx-react'
import { useContentStore, usePromotionStore } from '../../store/hooks/useStore'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { useGTMDispatch } from '../../analytics/react-gtm-hook'
import { useIntersectionObserver } from '../../hooks/use-intersection-observer'
import { CmsImage } from '../../components/cms/cms-image'

interface MealDealExplorerCardProps {
  promoID: string,
  isUpcoming?: boolean,
  slotIndex: number, // Index of the promotion within overall list of on page promotions used for analytics tracking
  listIndex: number // Index of the promotion within its slot used for analytics tracking
  pageName?: string // Name of the page the promotion is being rendered on
}

const MealDealExplorerCardContainer = ({
  promoID,
  isUpcoming = false,
  slotIndex,
  listIndex,
  pageName
}: MealDealExplorerCardProps) => {
  const cms = useContentStore()
  const mealDealBanner = promoID ? cms.contentById[promoID] : null
  const promotionStore = usePromotionStore()
  const promo = promotionStore.promotions[promoID]
  const ref = useRef<HTMLDivElement | null>(null)

  const startDate = promo?.startDate ? new Date(promo.startDate) : null
  const endDate = promo?.endDate ? new Date(promo?.endDate) : null

  let fullDateOutput = ''
  const closingText = 'Offer subject to availability.'

  if (startDate && endDate) {
    fullDateOutput = `Offer valid for delivery from ${format(
      startDate,
      'dd/MM/yyyy'
    )} until ${format(endDate, 'dd/MM/yyyy')}. `
  } else if (endDate) {
    fullDateOutput = `Offer valid for delivery until ${format(endDate, 'dd/MM/yyyy')}. `
  }

  fullDateOutput += closingText

  const comingSoonOutput =
    startDate && isUpcoming ? `Coming Soon: ${format(startDate, 'dd/MM/yyyy')}` : ''
  const dealUrl = isUpcoming ? '' : '/meal-deals?promo=' + promoID
  const ctaText = isUpcoming ? 'Coming Soon' : 'View This Deal'

  // GTM Tracking
  const {isOnScreen: isTrueView} = useIntersectionObserver(ref, {
    useOnce: true,
    /* Offset for intersection track. isOnScreen will only become true as the customer scrolls and the slot comes into view.
      -70px means isOnScreen will only become true after the customer can see at least 70px of the content, preventing the 
      tracking from firing before the customer has seen the content */
    rootMargin: '-70px 0px -70px 0px',
  })

  const sendDataToGTM = useGTMDispatch()

  const pageNameValue = pageName ? pageName + '_' : ''
  const creativeName = `${pageNameValue}meal_deal_card_${promo.name}`

  const promotionData = [
    {
      creative_name: promo.name,
      creative_slot: creativeName,
      promotion_id: dealUrl,
      promotion_name: ctaText,
      slot_position: slotIndex,
      link_position: listIndex,
      campaign_ID: '',
    },
  ]

  /**
   *  Track promotion view the first time card comes into the viewport.
   */
  useEffect(() => {
    if (!isTrueView || !sendDataToGTM || !promo) return

    try {
      setTimeout(() => {

        const eventData = {
          event: 'view_promotion',
          ecommerce: {
            items: promotionData,
          },
        }
        /*@ts-ignore Typescript has hard time of this, GTM dispatch will be a context by this point */
        sendDataToGTM(eventData)
      })
    } catch (error) {
      console.error('Failed to send promotion view data to GTM', error.message)
    }
  }, [isTrueView, promo])

  const sendSelectEventToGTM = () => {
    try {
      const eventData = {
        event: 'select_promotion',
        ecommerce: {
          items: promotionData,
        },
      }
      /*@ts-ignore GTM dispatch will be a context by this point */
      sendDataToGTM(eventData)
    } catch (error) {
      console.error('Failed to send promotion select data to GTM', error.message)
    }
  }

  return (
    <Flex
      as={isUpcoming ? 'div' : Link}
      {...(!isUpcoming && {to: dealUrl})}
      aria-label={promo?.name}
      bg="white"
      data-test-selector="promo-list-item"
      flexWrap="wrap"
      ref={ref}
      onClick={sendSelectEventToGTM}
    >
      {mealDealBanner && mealDealBanner?.image?.images.length && (
        <CmsImage images={mealDealBanner.image.images} useCrop='mobile' width="full" style={{objectFit: 'cover'}} />
      )}
      <Box padding="16px" paddingBottom="0px">
        <Text
          variant="text6"
          textAlign={['left']}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
          overflow="hidden"
          color={{default: 'gray800', hover: 'gray700'}}
          marginBottom={['12px', '8px']}
          lineHeight="short"
          data-test-selector="promo-list-item-name"
        >
          {promo?.name}
        </Text>
        <Text variant="text1" color="gray1000" marginBottom={['12px', '8px']} lineHeight="shorter">
          {fullDateOutput}
        </Text>
        {comingSoonOutput && (
          <Text
            display="inline-block"
            variant="text1"
            color="gray800"
            background="infoLight"
            borderRadius="medium"
            marginBottom={['16px']}
            lineHeight="shorter"
            opacity="1"
            paddingX="8px"
            paddingY="2px"
          >
            {comingSoonOutput}
          </Text>
        )}
      </Box>
      <Flex alignSelf="flex-end" padding="16px" paddingTop="0px" width="full">
        {isUpcoming ? (
          <Button pointerEvents="none" variant="outlineDark" size="md" width="full">
            {ctaText}
          </Button>
        ) : (
          <Button variant="outlinePrimary" size="md" width="full">
            {ctaText}
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export const MealDealExplorerCard = observer(MealDealExplorerCardContainer)